import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { buildAuthoringToolConfig, buildToolConfigOverride, defaultPanelState, defaultToolSelection, isAllowedSelectable, mergePanelState, toolLock, } from './internal/tool-config/delta.js';
const SELECTION_LABELS = {
    allowed: 'Always allow',
    needs_approval: 'Always ask',
    disabled: 'Disabled',
};
const SELECTION_ORDER = ['allowed', 'needs_approval', 'disabled'];
// ── Small, hand-authored controls (plain CSS) ─────────────────────────────
function ThreeStateControl({ value, onChange, allowedSelectable, disabled, label, }) {
    return (_jsx("div", { className: "sbui-tc-segmented", "aria-label": `${label} permission`, children: SELECTION_ORDER.map((option) => {
            const selected = value === option;
            // "Allowed" is offered only when the mode + baseline permit it.
            const optionDisabled = disabled || (option === 'allowed' && !allowedSelectable);
            return (_jsx("button", { type: "button", "aria-pressed": selected, disabled: optionDisabled, "data-testid": `perm-${option}`, className: "sbui-tc-segment", onClick: () => onChange(option), children: SELECTION_LABELS[option] }, option));
        }) }));
}
function Toggle({ checked, onChange, disabled, label, testId, }) {
    return (_jsx("button", { type: "button", role: "switch", "aria-checked": checked, "aria-label": label, disabled: disabled, "data-testid": testId, className: "sbui-tc-toggle", onClick: () => onChange(!checked), children: _jsx("span", { "aria-hidden": true, className: "sbui-tc-toggle-knob" }) }));
}
function LockBadge({ kind, }) {
    if (kind === 'always_deny') {
        return (_jsx("span", { className: "sbui-tc-badge sbui-tc-badge--locked", title: "The agent declares this tool always-deny; it is not callable and cannot be enabled.", children: "Locked" }));
    }
    // fn-74 — a DIRECT-connector tool: pinned at Always ask (reached outside the
    // gateway, so HITL is the only control), distinct from a session denylist.
    const title = kind === 'hitl_locked'
        ? 'This connector is configured directly, so its tools always require approval and cannot be set to Always allow.'
        : 'This tool is denylisted; it always requires approval and cannot be set to Always allow.';
    return (_jsx("span", { className: "sbui-tc-badge sbui-tc-badge--ask", title: title, children: "Always ask" }));
}
// ── Tool / connector / builtin rows ───────────────────────────────────────
function ToolRow({ tool, selection, mode, onChange, }) {
    const lock = toolLock(tool);
    // fn-74 — a DIRECT-connector tool (`always_allow_locked`) is per-tool
    // ENABLE/DISABLE only (NOT 3-state): its permission is FIXED at "Always ask"
    // (the lock badge), so the only choice is whether the tool is in scope. Enabled
    // maps to `needs_approval` (always-ask), disabled to `disabled`.
    const hitlLocked = lock === 'hitl_locked';
    return (_jsxs("div", { className: "sbui-tc-tool-row", "data-testid": `tool-row-${tool.tool_id}`, children: [_jsxs("div", { className: "sbui-tc-tool-meta", children: [_jsxs("div", { className: "sbui-tc-tool-name-line", children: [_jsx("span", { className: "sbui-tc-tool-name", children: tool.display_name }), lock !== null ? _jsx(LockBadge, { kind: lock }) : null] }), _jsx("span", { className: "sbui-tc-tool-server", children: tool.server_id })] }), hitlLocked ? (_jsx(Toggle, { checked: selection !== 'disabled', onChange: (next) => onChange(next ? 'needs_approval' : 'disabled'), label: `${tool.display_name} enabled`, testId: `tool-enable-${tool.tool_id}` })) : (_jsx(ThreeStateControl, { value: selection, onChange: onChange, allowedSelectable: isAllowedSelectable(tool, mode), disabled: lock === 'always_deny', label: tool.display_name }))] }));
}
const GROUP_META = [
    { key: 'write', title: 'Interactive tools' },
    { key: 'read', title: 'Read-only tools' },
    { key: 'unknown', title: 'Other tools' },
];
/**
 * Section the visible tools by `read_write` hint (the default — today's
 * behaviour, unchanged) or by federated `area_label` (fn-72 whole-gateway
 * authoring). Area grouping keys on `target_id` (stable, never re-derived from
 * the `tool_id` prefix) with first-seen order preserved (deterministic).
 */
function sectionTools(visible, groupBy) {
    if (groupBy === 'area') {
        const order = [];
        const byTarget = new Map();
        for (const t of visible) {
            const entry = byTarget.get(t.target_id);
            if (entry === undefined) {
                order.push(t.target_id);
                byTarget.set(t.target_id, { label: t.area_label, rows: [t] });
            }
            else {
                entry.rows.push(t);
            }
        }
        return order.map((targetId) => {
            const entry = byTarget.get(targetId);
            return {
                key: `area-${targetId}`,
                title: entry?.label ?? targetId,
                testId: `tool-group-area-${targetId}`,
                rows: entry?.rows ?? [],
            };
        });
    }
    return GROUP_META.map(({ key, title }) => ({
        key,
        title,
        testId: `tool-group-${key}`,
        rows: visible.filter((t) => t.read_write === key),
    }));
}
function ToolGroups({ connectors, connectorsDisabled, toolSelections, mode, groupBy, onToolChange, }) {
    // Flatten tools across ENABLED, enumerable connectors, then section them.
    const visible = [];
    for (const connector of connectors) {
        if (connector.catalog_unavailable)
            continue;
        if (connectorsDisabled[connector.server_id] === true)
            continue;
        visible.push(...connector.tools);
    }
    if (visible.length === 0)
        return null;
    return (_jsx("div", { className: "sbui-tc-groups", children: sectionTools(visible, groupBy).map(({ key, title, testId, rows }) => {
            if (rows.length === 0)
                return null;
            return (_jsxs("section", { className: "sbui-tc-group", "data-testid": testId, children: [_jsxs("h3", { className: "sbui-tc-group-title", children: [title, " (", rows.length, ")"] }), _jsx("div", { className: "sbui-tc-rows", children: rows.map((tool) => (_jsx(ToolRow, { tool: tool, mode: mode, selection: toolSelections[tool.tool_id] ?? defaultToolSelection(tool), onChange: (next) => onToolChange(tool.tool_id, next) }, tool.tool_id))) })] }, key));
        }) }));
}
function ConnectorList({ connectors, connectorsDisabled, onConnectorToggle, }) {
    return (_jsxs("section", { className: "sbui-tc-section", "data-testid": "connector-list", children: [_jsx("h3", { className: "sbui-tc-section-title", children: "Connectors" }), _jsx("ul", { className: "sbui-tc-list", children: connectors.map((connector) => {
                    const enabled = connectorsDisabled[connector.server_id] !== true;
                    return (_jsxs("li", { className: "sbui-tc-list-row", "data-testid": `connector-${connector.server_id}`, children: [_jsxs("div", { className: "sbui-tc-list-meta", children: [_jsx("span", { className: "sbui-tc-list-name sbui-tc-mono", children: connector.server_id }), connector.login_required === true ? (
                                    // fn-74 (R4/R6) — a DIRECT connector (InfoTrack) with no firm
                                    // token: per-tool config needs a login first, but the
                                    // whole-server enable/disable (the fn-73 always-ask fallback)
                                    // is still available right here.
                                    _jsx("p", { className: "sbui-tc-list-note", "data-testid": `connector-login-required-${connector.server_id}`, children: "Log in to InfoTrack first to configure its tools \u2014 or enable the whole connector (every call asks for approval)." })) : connector.catalog_unavailable ? (_jsx("p", { className: "sbui-tc-list-note", "data-testid": `connector-degraded-${connector.server_id}`, children: "Tool list unavailable \u2014 enable or disable the whole connector." })) : (_jsxs("p", { className: "sbui-tc-list-note", children: [connector.tools.length, " tool", connector.tools.length === 1 ? '' : 's'] }))] }), _jsx(Toggle, { checked: enabled, onChange: (next) => onConnectorToggle(connector.server_id, next), label: `${connector.server_id} connector`, testId: `connector-toggle-${connector.server_id}` })] }, connector.server_id));
                }) })] }));
}
function BuiltinList({ builtins, builtinsEnabled, mode, onBuiltinToggle, }) {
    return (_jsxs("section", { className: "sbui-tc-section", "data-testid": "builtin-list", children: [_jsx("h3", { className: "sbui-tc-section-title", children: "Web & built-in tools" }), _jsx("ul", { className: "sbui-tc-list", children: builtins.map((b) => {
                    const enabled = builtinsEnabled[b.name] ?? b.baseline_enabled;
                    // narrowing-only: a baseline-disabled built-in can never be turned on.
                    // authoring: the author MAY enable a baseline-disabled built-in.
                    const lockedOff = mode === 'narrowing' && !b.baseline_enabled;
                    return (_jsxs("li", { className: "sbui-tc-list-row", "data-testid": `builtin-${b.name}`, children: [_jsxs("div", { className: "sbui-tc-list-meta", children: [_jsx("span", { className: "sbui-tc-list-name", children: prettyBuiltin(b.name) }), _jsxs("p", { className: "sbui-tc-list-note", children: ["Runs in the agent sandbox", lockedOff ? ' — not enabled for this agent' : '', "."] })] }), _jsx(Toggle, { checked: enabled, onChange: (next) => onBuiltinToggle(b.name, next), disabled: lockedOff, label: `${prettyBuiltin(b.name)} built-in`, testId: `builtin-toggle-${b.name}` })] }, b.name));
                }) })] }));
}
function prettyBuiltin(name) {
    return name
        .replace(/^web_/, 'Web ')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
const EMPTY_AUTHORING = {
    servers: {},
    nativeBuiltins: {},
    catalogAvailable: true,
};
export function AgentToolConfigPanel({ loadState, agentKey, mode, groupBy = 'read_write', onRetry, onOverrideChange, onAuthoringChange, onStateChange, initialStateBuilder = defaultPanelState, }) {
    const data = loadState.kind === 'ready' ? loadState.data : null;
    // `stateKey` is the `agentKey` the current `state` was seeded for, updated
    // ATOMICALLY with `state` (both in the same render-time reset). The emit
    // effect uses it to tell when `state` still belongs to the PREVIOUS agent.
    const [stateKey, setStateKey] = React.useState(() => data === null ? null : agentKey);
    const [state, setState] = React.useState(() => data === null ? null : initialStateBuilder(data));
    // ── Synchronous (render-time) reset on a genuine agent switch ──
    // Memory `per-session-reducer-reset-must-be` synchronous: a passive-effect
    // reset is too late — on a ready→ready switch the emit effect would run in the
    // SAME commit with the previous agent's selections under the new agent's data
    // (a stale callback, esp. when two agents share tool ids). Adjusting state
    // during render makes React discard this render (its effects never fire) and
    // re-render with fresh state, so no stale result is ever emitted. The guard is
    // keyed on `agentKey` (a string), never on `data` identity, so it can't loop.
    if (stateKey !== agentKey) {
        setStateKey(agentKey);
        setState(data === null ? null : initialStateBuilder(data));
    }
    // Surface a fresh "pure baseline" result up so the parent never holds the
    // previous agent's edits. Stable across renders.
    const emitEmpty = React.useCallback(() => {
        onOverrideChange?.({});
        onAuthoringChange?.(EMPTY_AUTHORING);
    }, [onOverrideChange, onAuthoringChange]);
    // On a switch to an agent whose config is NOT ready yet (loading/error), emit
    // an empty (pure-baseline) result ONCE — so the parent drops the prior agent's
    // selections while the new config loads. Guarded per-agentKey so a SAME-agent
    // refetch that briefly drops to loading (state preserved) does NOT clobber the
    // last real value.
    const emptyEmittedFor = React.useRef(null);
    React.useEffect(() => {
        if (data === null && state === null && stateKey === agentKey) {
            if (emptyEmittedFor.current !== agentKey) {
                emptyEmittedFor.current = agentKey;
                emitEmpty();
            }
        }
    }, [data, state, stateKey, agentKey, emitEmpty]);
    // Same-agent data refresh (e.g. a degraded → recovered retry that surfaces
    // tools the first response omitted): MERGE prior edits onto the fresh seed,
    // using the SAME builder as the seed so a newly-arrived tool can't seed
    // allowed under an applied disable. Also seeds state when a freshly-switched
    // agent's data finally arrives after a loading gap (prev === null).
    React.useEffect(() => {
        if (data === null)
            return;
        if (stateKey !== agentKey)
            return; // reset not yet committed; wait for it
        setState((prev) => prev === null
            ? initialStateBuilder(data)
            : mergePanelState(data, prev, initialStateBuilder, mode));
    }, [data, stateKey, agentKey, initialStateBuilder, mode]);
    // Surface the built result upward whenever the panel state changes. The mode
    // selects which builder + callback (both callbacks are optional — a consumer
    // wires only the one it needs). `stateKey === agentKey` guarantees `state`
    // belongs to the CURRENT agent (never a stale mid-switch emit).
    React.useEffect(() => {
        if (data === null || state === null)
            return;
        if (stateKey !== agentKey)
            return;
        emptyEmittedFor.current = null; // a real value supersedes any prior empty
        onStateChange?.(state);
        if (mode === 'narrowing') {
            onOverrideChange?.(buildToolConfigOverride(data, state));
        }
        else {
            onAuthoringChange?.(buildAuthoringToolConfig(data, state));
        }
    }, [data, state, stateKey, agentKey, mode, onOverrideChange, onAuthoringChange, onStateChange]);
    if (loadState.kind === 'loading') {
        return (_jsx("div", { className: "sbui-tc-skeleton", "aria-busy": true, "data-testid": "tool-config-loading", children: [0, 1, 2].map((i) => (_jsx("div", { className: "sbui-tc-skeleton-row" }, i))) }));
    }
    if (loadState.kind === 'error') {
        return (_jsxs("div", { className: "sbui-tc-alert sbui-tc-alert--error", role: "alert", "data-testid": "tool-config-error", children: [_jsx("p", { className: "sbui-tc-alert-title", children: "Couldn\u2019t load tools" }), _jsx("p", { className: "sbui-tc-alert-body", children: loadState.message }), _jsx("button", { type: "button", className: "sbui-tc-btn", onClick: onRetry, children: "Retry" })] }));
    }
    // ready
    if (data === null || state === null) {
        // Defensive: ready but state not yet seeded (first render before effect).
        return _jsx("div", { "aria-hidden": true, "data-testid": "tool-config-priming" });
    }
    const noConnectors = data.connectors.length === 0;
    const noBuiltins = data.native_builtins.length === 0;
    if (noConnectors && noBuiltins) {
        // Distinguish a genuine empty (catalog read fine, agent simply declares no
        // configurable tools) from a DEGRADED empty (catalog couldn't be read, so
        // the list may be hiding tools) — the latter must be a visible alert with a
        // Retry, never a confident "nothing here" (memory
        // `secondary-query-joins-must-branch-on`).
        if (!data.catalog_available) {
            return (_jsxs("div", { className: "sbui-tc-alert sbui-tc-alert--warn", role: "alert", "data-testid": "tool-config-degraded-empty", children: [_jsx("p", { className: "sbui-tc-alert-title", children: "Tools couldn\u2019t be loaded" }), _jsx("p", { className: "sbui-tc-alert-body", children: "The tool catalog is unavailable, so this agent\u2019s tools can\u2019t be shown right now. Try again." }), _jsx("button", { type: "button", className: "sbui-tc-btn", onClick: onRetry, children: "Retry" })] }));
        }
        return (_jsx("div", { className: "sbui-tc-empty", "data-testid": "tool-config-empty", children: "This agent has no configurable tools." }));
    }
    return (_jsxs("div", { className: "sbui-tc", "data-testid": "tool-config-ready", children: [data.catalog_available ? null : (_jsx("p", { className: "sbui-tc-banner", "aria-live": "polite", "data-testid": "catalog-degraded-banner", children: "Some tool details couldn\u2019t be loaded; permissions still apply. You can still enable or disable connectors and set approvals." })), noConnectors ? null : (_jsx(ConnectorList, { connectors: data.connectors, connectorsDisabled: state.connectorsDisabled, onConnectorToggle: (serverId, enabled) => setState((prev) => prev === null
                    ? prev
                    : {
                        ...prev,
                        connectorsDisabled: Object.assign(Object.create(null), prev.connectorsDisabled, {
                            [serverId]: !enabled,
                        }),
                    }) })), _jsx(ToolGroups, { connectors: data.connectors, connectorsDisabled: state.connectorsDisabled, toolSelections: state.toolSelections, mode: mode, groupBy: groupBy, onToolChange: (toolId, next) => setState((prev) => prev === null
                    ? prev
                    : {
                        ...prev,
                        toolSelections: Object.assign(Object.create(null), prev.toolSelections, {
                            [toolId]: next,
                        }),
                    }) }), noBuiltins ? null : (_jsx(BuiltinList, { builtins: data.native_builtins, builtinsEnabled: state.builtinsEnabled, mode: mode, onBuiltinToggle: (name, enabled) => setState((prev) => prev === null
                    ? prev
                    : {
                        ...prev,
                        builtinsEnabled: Object.assign(Object.create(null), prev.builtinsEnabled, {
                            [name]: enabled,
                        }),
                    }) }))] }));
}
//# sourceMappingURL=agent-tool-config-panel.js.map